import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../utils/api';
import { isHybridMode } from '../utils/config';

/**
 * Resolves whether the current user may submit a map tip.
 *
 * The server is always authoritative (Django re-checks on POST), this
 * hook only drives UI visibility. Anonymous users get ``eligible=false``
 * with ``reason='auth_required'`` without hitting the API, so the entry
 * point can stay hidden on the public stats site.
 *
 * @returns {{eligible: boolean, reason: string, minMatches: number, loading: boolean}}
 */
export default function useCanSuggestMap() {
  const { isLoggedIn, getAuthHeaders } = useAuth();
  const [state, setState] = useState({
    eligible: false,
    reason: 'auth_required',
    minMatches: 5,
    loading: true,
  });

  useEffect(() => {
    if (!isHybridMode || !isLoggedIn) {
      setState({ eligible: false, reason: 'auth_required', minMatches: 5, loading: false });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/api/maps/suggestions/can-suggest`, {
          headers: getAuthHeaders(),
        });
        if (!resp.ok) {
          if (!cancelled) {
            setState({ eligible: false, reason: 'auth_required', minMatches: 5, loading: false });
          }
          return;
        }
        const data = await resp.json();
        if (!cancelled) {
          setState({
            eligible: !!data.eligible,
            reason: data.reason || '',
            minMatches: data.min_matches ?? 5,
            loading: false,
          });
        }
      } catch {
        if (!cancelled) {
          setState((s) => ({ ...s, loading: false }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isLoggedIn, getAuthHeaders]);

  return state;
}
